(() => {
  if (!customElements.get('search-form') && window.SearchForm) {
    customElements.define('search-form', window.SearchForm);
  }

  if (customElements.get('predictive-search')) return;

  const BaseSearchForm = window.SearchForm || HTMLElement;

  class PredictiveSearch extends BaseSearchForm {
    constructor() {
      super();
      this.cachedResults = {};
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.statusElement = this.querySelector('.predictive-search-status');
      this.loadingText = this.getAttribute('data-loading-text') || '';
      this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
      this.isOpen = false;
      this.abortController = new AbortController();
      this.searchTerm = '';

      if (this.input) {
        const onInput = window.debounce
          ? window.debounce(() => this.onChange(), 300)
          : () => this.onChange();
        this.input.addEventListener('input', onInput);
        this.input.addEventListener('focus', this.onFocus.bind(this));
      }
      this.addEventListener('focusout', this.onFocusOut.bind(this));
      this.addEventListener('keyup', this.onKeyup.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
    }

    getQuery() {
      return this.input ? this.input.value.trim() : '';
    }

    onChange() {
      if (this.toggleResetButton) this.toggleResetButton();
      this.searchTerm = this.getQuery();

      if (!this.searchTerm.length) {
        this.close(true);
        return;
      }

      this.getSearchResults(this.searchTerm);
    }

    onFormReset(event) {
      if (super.onFormReset) super.onFormReset(event);
      if (!this.getQuery().length) this.close(true);
    }

    onFocus() {
      const currentSearchTerm = this.getQuery();
      if (!currentSearchTerm.length) return;

      if (this.searchTerm !== currentSearchTerm) {
        this.onChange();
      } else if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getSearchResults(this.searchTerm);
      }
    }

    onFocusOut() {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      });
    }

    onKeyup(event) {
      if (!this.getAttribute('open')) return;

      if (event.code === 'ArrowUp') {
        event.preventDefault();
        this.switchOption('up');
      } else if (event.code === 'ArrowDown') {
        event.preventDefault();
        this.switchOption('down');
      } else if (event.code === 'Enter') {
        this.selectOption();
      } else if (event.code === 'Escape') {
        this.close();
      }
    }

    onKeydown(event) {
      if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
    }

    switchOption(direction) {
      if (!this.predictiveSearchResults) return;

      const moveUp = direction === 'up';
      const selected = this.predictiveSearchResults.querySelector('[aria-selected="true"]');
      const visibleOptions = Array.from(
        this.predictiveSearchResults.querySelectorAll('li[role="option"], button[role="option"]')
      ).filter((element) => element.offsetParent !== null);

      if (!visibleOptions.length) return;
      if (moveUp && !selected) return;

      let selectedIndex = selected ? visibleOptions.indexOf(selected) : -1;
      selected?.setAttribute('aria-selected', 'false');

      let nextIndex;
      if (moveUp) {
        nextIndex = selectedIndex <= 0 ? visibleOptions.length - 1 : selectedIndex - 1;
      } else {
        nextIndex = selectedIndex === visibleOptions.length - 1 ? 0 : selectedIndex + 1;
      }

      const nextOption = visibleOptions[nextIndex];
      nextOption.setAttribute('aria-selected', 'true');
      if (this.input) this.input.setAttribute('aria-activedescendant', nextOption.id || '');
      nextOption.scrollIntoView?.({ block: 'nearest' });
    }

    selectOption() {
      const selected = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
      selected?.click();
    }

    getSearchResults(searchTerm) {
      const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
      this.setLiveRegionLoadingState();

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }

      this.abortController.abort();
      this.abortController = new AbortController();

      fetch(
        `${window.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`,
        { signal: this.abortController.signal }
      )
        .then((response) => {
          if (!response.ok) {
            this.close();
            throw new Error(String(response.status));
          }
          return response.text();
        })
        .then((text) => {
          const section = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-predictive-search');
          const markup = section ? section.innerHTML : '';

          this.allPredictiveSearchInstances.forEach((instance) => {
            instance.cachedResults[queryKey] = markup;
          });

          this.renderSearchResults(markup);
        })
        .catch((error) => {
          if (error?.name === 'AbortError') return;
          this.close();
        });
    }

    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
      this.setAttribute('loading', true);
    }

    setLiveRegionText(text) {
      if (!this.statusElement) return;
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = text;
      window.setTimeout(() => this.statusElement?.setAttribute('aria-hidden', 'true'), 1000);
    }

    renderSearchResults(markup) {
      if (!this.predictiveSearchResults) return;
      this.predictiveSearchResults.innerHTML = markup;
      this.setAttribute('results', true);

      const liveCount = this.querySelector('[data-predictive-search-live-region-count-value]');
      this.setLiveRegionText(liveCount ? liveCount.textContent : '');
      this.open();
    }

    open() {
      this.setAttribute('open', true);
      if (this.input) this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;
    }

    close(clearSearchTerm = false) {
      if (clearSearchTerm && this.input) {
        this.input.value = '';
        this.removeAttribute('results');
      }
      if (this.input) {
        this.input.setAttribute('aria-activedescendant', '');
        this.input.setAttribute('aria-expanded', false);
      }
      this.removeAttribute('loading');
      this.removeAttribute('open');
      this.statusElement?.setAttribute('aria-hidden', 'true');
      this.isOpen = false;
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
})();
